// components/Dashboard/DashboardTemplates.jsx
import React, { useState } from 'react';
import { X, Check } from 'lucide-react';
import { useStore } from '../../store/useStore';

export const TEMPLATES = [
  {
    id: 'infrastructure',
    name: 'Infrastructure Overview',
    description: 'Full system health: log volumes, error rates, gauges, source heatmap, and live streams.',
    tags: ['ops', 'sre', 'infra'],
    defaultVariables: { source: 'ALL', severity: 'ALL' },
    preview: [
      [
        { f: 1, h: 14, c: '#3b82f6' }, { f: 1, h: 14, c: '#3b82f6' }, { f: 1, h: 14, c: '#3b82f6' },
        { f: 1, h: 14, c: '#3b82f6' }, { f: 1, h: 14, c: '#3b82f6' }, { f: 1, h: 14, c: '#3b82f6' },
      ],
      [{ f: 3, h: 24, c: '#6366f1' }, { f: 2, h: 24, c: '#8b5cf6' }],
      [{ f: 1, h: 22, c: '#22c55e' }, { f: 1, h: 22, c: '#22c55e' }, { f: 2, h: 22, c: '#f59e0b' }],
      [{ f: 1, h: 14, c: '#ef4444' }],
      [{ f: 1, h: 22, c: '#475569' }, { f: 1, h: 22, c: '#374151' }],
    ],
  },
  {
    id: 'apm',
    name: 'Application Performance',
    description: 'Error trends, latency, throughput, source breakdown, and error log stream.',
    tags: ['apm', 'dev', 'perf'],
    defaultVariables: { source: 'ALL', severity: 'ALL' },
    preview: [
      [
        { f: 1, h: 14, c: '#3b82f6' }, { f: 1, h: 14, c: '#ef4444' },
        { f: 1, h: 14, c: '#8b5cf6' }, { f: 1, h: 14, c: '#f59e0b' },
      ],
      [{ f: 1, h: 28, c: '#6366f1' }],
      [{ f: 3, h: 22, c: '#f59e0b' }, { f: 1, h: 22, c: '#8b5cf6' }],
      [{ f: 1, h: 22, c: '#475569' }],
      [{ f: 1, h: 22, c: '#374151' }],
    ],
  },
  {
    id: 'security',
    name: 'Security & Compliance',
    description: 'Critical alerts, suspicious events, audit log stream, and security event timeline.',
    tags: ['security', 'audit', 'soc'],
    defaultVariables: { source: 'ALL', severity: 'CRITICAL' },
    preview: [
      [
        { f: 1, h: 14, c: '#ef4444' }, { f: 1, h: 14, c: '#ef4444' },
        { f: 1, h: 14, c: '#3b82f6' }, { f: 1, h: 14, c: '#22c55e' },
      ],
      [{ f: 1, h: 28, c: '#374151' }],
      [{ f: 1, h: 22, c: '#22c55e' }, { f: 3, h: 22, c: '#8b5cf6' }],
      [{ f: 1, h: 14, c: '#ef444450' }],
      [{ f: 1, h: 22, c: '#475569' }],
    ],
  },
];

function PreviewGrid({ rows }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, padding: 8 }}>
      {rows.map((row, i) => (
        <div key={i} style={{ display: 'flex', gap: 3 }}>
          {row.map((cell, j) => (
            <div
              key={j}
              style={{
                flex: cell.f,
                height: cell.h,
                borderRadius: 3,
                background: cell.c + '55',
                border: `1px solid ${cell.c}70`,
                flexShrink: 0,
              }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

export function DashboardTemplates({ onClose }) {
  const { activeTemplate, setActiveTemplate, setVariable } = useStore();
  const [selected, setSelected] = useState(activeTemplate);

  function apply() {
    const tpl = TEMPLATES.find(t => t.id === selected);
    if (tpl) {
      setActiveTemplate(selected);
      if (tpl.defaultVariables) {
        Object.entries(tpl.defaultVariables).forEach(([k, v]) => setVariable(k, v));
      }
    }
    onClose();
  }

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 600,
        background: 'rgba(0,0,0,.65)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--bg-secondary)',
          border: '1px solid var(--border)',
          borderRadius: 14,
          padding: 24,
          width: 760,
          maxWidth: 'calc(100vw - 32px)',
          boxShadow: '0 24px 80px rgba(0,0,0,.6)',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>Dashboard Templates</div>
            <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>
              Choose a pre-built layout for your monitoring use case
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--text-3)', padding: 4, borderRadius: 6,
            }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Template cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
          {TEMPLATES.map(tpl => {
            const isSelected = selected === tpl.id;
            return (
              <div
                key={tpl.id}
                onClick={() => setSelected(tpl.id)}
                style={{
                  border: `2px solid ${isSelected ? '#3b82f6' : 'var(--border)'}`,
                  borderRadius: 10,
                  cursor: 'pointer',
                  background: isSelected ? 'rgba(59,130,246,.08)' : 'var(--bg-tertiary)',
                  transition: 'all .15s',
                  overflow: 'hidden',
                  position: 'relative',
                }}
              >
                {/* Selected check */}
                {isSelected && (
                  <div style={{
                    position: 'absolute', top: 8, right: 8,
                    width: 20, height: 20, borderRadius: '50%',
                    background: '#3b82f6',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    zIndex: 1,
                  }}>
                    <Check size={11} color="white" />
                  </div>
                )}

                {/* Preview */}
                <div style={{
                  background: 'var(--bg-primary)',
                  borderBottom: '1px solid var(--border)',
                  minHeight: 120,
                }}>
                  <PreviewGrid rows={tpl.preview} />
                </div>

                {/* Info */}
                <div style={{ padding: '10px 12px' }}>
                  <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 4 }}>{tpl.name}</div>
                  <div style={{ fontSize: 10, color: 'var(--text-3)', lineHeight: 1.5, marginBottom: 8 }}>
                    {tpl.description}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {tpl.tags.map(tag => (
                      <span key={tag} style={{
                        fontSize: 9, padding: '2px 6px', borderRadius: 4,
                        background: 'rgba(99,102,241,.15)',
                        color: '#818cf8',
                        fontFamily: "'Space Mono', monospace",
                        letterSpacing: '0.5px',
                        textTransform: 'uppercase',
                      }}>{tag}</span>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            onClick={onClose}
            style={{
              padding: '7px 18px', borderRadius: 7, fontSize: 12,
              border: '1px solid var(--border)', background: 'none',
              color: 'var(--text-2)', cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={apply}
            style={{
              padding: '7px 18px', borderRadius: 7, fontSize: 12,
              border: 'none', background: '#3b82f6',
              color: 'white', cursor: 'pointer', fontWeight: 600,
            }}
          >
            Apply Template
          </button>
        </div>
      </div>
    </div>
  );
}
